import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogTitle from "@mui/material/DialogTitle"
import ArrowBack from "@mui/icons-material/ArrowBack"
import ArrowForward from "@mui/icons-material/ArrowForward"
import CloseIcon from "@mui/icons-material/Close"
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded"
import IconButton from "@mui/material/IconButton"
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Chip from "@mui/material/Chip"
import { alpha, useTheme } from "@mui/material/styles"
import { useState } from "react"
import { PAGES, pagesData } from "./pageData"
import { PageSection } from "./PageSection"

interface OnboardModalProps
{
  open: boolean
  handleClose: () => void
}

export const OnboardModal = ({ open, handleClose }: OnboardModalProps) => {
  const theme = useTheme()
  const [ index, setIndex ] = useState<PAGES>(PAGES.WELCOME)

  const handleFoward = () => {
    if(index < PAGES.BACKTESTING) setIndex(index + 1)
  }

  const handleBack = () => {
    if(index > PAGES.WELCOME) setIndex(index - 1)
  }

  const stepItems = Object.values(pagesData).map((page, stepIndex) => ({
    title: page.title,
    active: index === stepIndex,
    completed: index > stepIndex,
  }))

  return(
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: styles.dialog(theme) }}
    >
      <DialogTitle sx={styles.titleBar(theme)}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Box sx={styles.headerIcon(theme)}>
              <AutoAwesomeRoundedIcon />
            </Box>
          </Grid>
          <Grid item xs>
            <Typography variant="h4" fontWeight={700}>Welcome</Typography>
            <Typography variant="body2" sx={styles.subtitle(theme)}>
              Get AlgoTrader configured faster with a guided onboarding flow.
            </Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={handleClose} sx={styles.closeButton(theme)}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <Box sx={styles.body(theme)}>
        <Box sx={styles.progressWrap(theme)}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} useFlexGap flexWrap="wrap">
            {stepItems.map((step, stepIndex) => (
              <Box key={step.title} sx={styles.stepCard(theme, step.active)}>
                <Chip
                  label={step.completed ? "Done" : `${stepIndex + 1}`}
                  size="small"
                  sx={styles.stepChip(theme, step.active, step.completed)}
                />
                <Typography variant="body2" fontWeight={step.active ? 700 : 600} sx={{ minWidth: 0 }}>
                  {step.title}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Box sx={styles.contentWrap}>
          <PageSection pageSection={pagesData[index as number]} />
        </Box>
      </Box>
     
      <DialogActions sx={styles.actions(theme)}>
        { index !== PAGES.WELCOME && <Button startIcon={<ArrowBack />} onClick={handleBack}>Back</Button> }
        <div style={{ flex: 1 }} />
        { index !== PAGES.BACKTESTING &&  <Button variant="contained" sx={styles.primaryButton} endIcon={<ArrowForward />} onClick={handleFoward}>Next</Button> }
        { index === PAGES.BACKTESTING &&  <Button variant="contained" sx={styles.primaryButton} onClick={handleClose}>Done</Button> }
      </DialogActions>
    </Dialog>
  )
}

const styles = {
  dialog: (theme: any) => ({
    borderRadius: "22px",
    overflow: "hidden",
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(180deg, ${alpha("#0f1c2d", 0.99)} 0%, ${alpha("#09111d", 0.99)} 100%)`
        : `linear-gradient(180deg, ${alpha("#ffffff", 0.99)} 0%, ${alpha("#edf5ff", 0.98)} 100%)`,
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
  }),
  titleBar: (theme: any) => ({
    px: 3,
    pt: 3,
    pb: 2,
    borderBottom: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
  }),
  headerIcon: (theme: any) => ({
    width: 48,
    height: 48,
    borderRadius: "14px",
    display: "grid",
    placeItems: "center",
    color: theme.palette.info.main,
    backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.16 : 0.14),
  }),
  subtitle: (theme: any) => ({
    color: alpha(theme.palette.text.primary, 0.68),
  }),
  closeButton: (theme: any) => ({
    width: 42,
    height: 42,
    borderRadius: "12px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.04 : 0.7),
  }),
  body: (theme: any) => ({
    padding: 2,
  }),
  progressWrap: (theme: any) => ({
    marginBottom: 1,
  }),
  stepCard: (theme: any, active: boolean) => ({
    minWidth: 0,
    padding: "10px 12px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    gap: 1,
    backgroundColor: active
      ? alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.14 : 0.08)
      : alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.54),
    border: `1px solid ${active ? alpha(theme.palette.info.main, 0.38) : alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.06 : 0.4)}`,
  }),
  stepChip: (theme: any, active: boolean, completed: boolean) => ({
    borderRadius: "999px",
    minWidth: 42,
    backgroundColor: completed
      ? alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.22 : 0.15)
      : active
        ? alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.22 : 0.15)
        : alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.72),
    '& .MuiChip-label': {
      fontWeight: 700,
    },
  }),
  contentWrap: {
    display: "block",
  },
  actions: (theme: any) => ({
    px: 2,
    py: 1.5,
    borderTop: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.14 : 0.02),
  }),
  primaryButton: {
    minWidth: 120,
    borderRadius: "12px",
    boxShadow: "none",
  },
}
